// /me/rankings — the signed-in user's Custom Rankings, newest first, in two
// tabs: Published (with an "Unpublished changes" badge when there are private
// edits) and Drafts (only the owner sees them; incomplete ones show "6 of 10
// selected"). Paginated by the API cursor, per tab. `?tab=drafts` opens Drafts.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { rankingProgressLabel } from '@aluvo/constants/src/rankings';
import { RankingService, getRankingErrorMessage } from '../../services/rankingService';
import base from '../TopMovies/TopMovies.module.css';
import styles from './MyRankings.module.css';

const COMPOSITION_LABEL = { movie: 'Movies', tv: 'TV', mixed: 'Movies & TV' };
const TABS = [
    { key: 'published', label: 'Published' },
    { key: 'draft', label: 'Drafts' },
];

const titleOf = (ranking) => ranking.title || 'Untitled draft';

function RankingRow({ ranking, onUnpublish, busy }) {
    const published = ranking.status === 'published';
    const count = ranking.itemCount ?? ranking.size;
    return (
        <li className={styles.row}>
            <Link to={`/rankings/${ranking.id}/edit`} className={styles.rowMain}>
                <span className={styles.posters} aria-hidden="true">
                    {ranking.previewPosters.slice(0, 5).map((url) => <img key={url} src={url} alt="" loading="lazy" />)}
                    {ranking.previewPosters.length === 0 && <span className={styles.noPoster}>🏆</span>}
                </span>
                <span className={styles.rowText}>
                    <strong>{titleOf(ranking)}</strong>
                    <span className={styles.meta}>
                        Top {ranking.size}{ranking.composition ? ` · ${COMPOSITION_LABEL[ranking.composition]}` : ''}
                        {' · '}{published ? 'Published' : count < ranking.size ? rankingProgressLabel(count, ranking.size) : 'Ready to publish'}
                    </span>
                    {published && ranking.hasUnpublishedChanges && <span className={styles.badge}>Unpublished changes</span>}
                </span>
            </Link>
            <span className={styles.rowActions}>
                <Link to={`/rankings/${ranking.id}/edit`} className={base.ghost} aria-label={`Edit ${titleOf(ranking)}`}>Edit</Link>
                {published && ranking.shareSlug && <Link to={`/rankings/${ranking.shareSlug}`} className={base.ghost} aria-label={`View public page for ${titleOf(ranking)}`}>View</Link>}
                {published && (
                    <button type="button" className={base.ghost} onClick={() => onUnpublish(ranking)} disabled={busy} aria-label={`Unpublish ${titleOf(ranking)}`}>Unpublish</button>
                )}
            </span>
        </li>
    );
}

export default function MyRankings() {
    const location = useLocation();
    const [tab, setTab] = useState(() => (new URLSearchParams(location.search || '').get('tab') === 'drafts' ? 'draft' : 'published'));
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const loadingMoreRef = useRef(false);
    const ticket = useRef(0);
    const deleted = location.state?.deleted;

    const loadFirst = useCallback(async () => {
        const mine = ++ticket.current;
        setStatus('loading');
        setItems([]);
        try {
            const page = await RankingService.listMine({ limit: 20, status: tab });
            if (mine !== ticket.current) return; // the tab changed meanwhile
            setItems(page.items || []);
            setNextCursor(page.nextCursor || null);
            setStatus('ready');
        } catch (err) {
            if (mine !== ticket.current) return;
            setError(getRankingErrorMessage(err, "Couldn't load your rankings."));
            setStatus('error');
        }
    }, [tab]);

    useEffect(() => { loadFirst(); }, [loadFirst]);
    useEffect(() => { document.title = 'My Rankings | Aluvo'; }, []);

    async function loadMore() {
        if (!nextCursor || loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const page = await RankingService.listMine({ limit: 20, cursor: nextCursor, status: tab });
            setItems((prev) => {
                const seen = new Set(prev.map((r) => r.id));
                return [...prev, ...(page.items || []).filter((r) => !seen.has(r.id))];
            });
            setNextCursor(page.nextCursor || null);
        } catch (err) {
            setError(getRankingErrorMessage(err, "Couldn't load more rankings."));
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }

    async function unpublish(ranking) {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Unpublish "${titleOf(ranking)}"? Its link and profile listing stop showing it. It moves to Drafts, and publishing it again brings back the same link.`)) return;
        setBusyId(ranking.id);
        setError('');
        try {
            await RankingService.unpublish(ranking.id);
            setItems((prev) => prev.filter((r) => r.id !== ranking.id));
            setNotice(`Unpublished “${titleOf(ranking)}”. It's in Drafts now.`);
        } catch (err) {
            setError(getRankingErrorMessage(err, "Couldn't unpublish this ranking. Please try again."));
        } finally {
            setBusyId(null);
        }
    }

    const emptyCopy = tab === 'draft'
        ? { heading: 'No drafts.', body: 'Save a ranking as a draft to finish it later — only you can see drafts.' }
        : { heading: 'Rank anything you love.', body: 'Your Top 5 or Top 10 — comedies, anime, TV shows, a director\'s films or all-time greats. Publish it and see who makes their own.' };

    return (
        <main className={base.page}>
            <div className={base.shell}>
                <header className={styles.header}>
                    <div>
                        <span className={base.eyebrow}>Aluvo · Rankings</span>
                        <h1>My Rankings</h1>
                    </div>
                    <Link to="/rankings/new" state={{ source: 'my_rankings' }} className={base.primary}>+ Create a ranking</Link>
                </header>

                <div className={styles.tabs} role="tablist" aria-label="My rankings">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            id={`rankings-tab-${t.key}`}
                            aria-selected={tab === t.key}
                            aria-controls="rankings-tabpanel"
                            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                            onClick={() => { setNotice(''); setTab(t.key); }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div id="rankings-tabpanel" role="tabpanel" aria-labelledby={`rankings-tab-${tab}`}>
                    {deleted && <p className={base.notice} role="status">Deleted “{deleted}”.</p>}
                    {notice && <p className={base.notice} role="status">{notice}</p>}
                    {status === 'loading' && <p className={base.muted} role="status">Loading your rankings…</p>}
                    {status === 'error' && (
                        <p className={base.errorText} role="alert">
                            {error}{' '}
                            <button type="button" className={base.linkButton} onClick={loadFirst}>Try again</button>
                        </p>
                    )}
                    {status === 'ready' && error && <p className={base.errorText} role="alert">{error}</p>}
                    {status === 'ready' && items.length === 0 && (
                        <section className={styles.empty}>
                            <h2>{emptyCopy.heading}</h2>
                            <p>{emptyCopy.body}</p>
                            <Link to="/rankings/new" state={{ source: 'my_rankings' }} className={base.primary}>{tab === 'draft' ? 'Start a ranking' : 'Create your first ranking'}</Link>
                        </section>
                    )}
                    {items.length > 0 && (
                        <>
                            <ul className={styles.list} aria-label={tab === 'draft' ? 'My drafts' : 'My published rankings'}>
                                {items.map((ranking) => <RankingRow key={ranking.id} ranking={ranking} onUnpublish={unpublish} busy={busyId === ranking.id} />)}
                            </ul>
                            {nextCursor && (
                                <button type="button" className={`${base.secondary} ${styles.more}`} onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading…' : 'Load more'}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
